import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI } from '../services/api';

const AuthContext = createContext();

/**
 * Decode JWT payload without a library (base64url → JSON).
 * Returns null if token is malformed.
 */
function decodeTokenPayload(token) {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(payload));
  } catch {
    return null;
  }
}

/**
 * Check whether a stored JWT is still valid (not expired).
 */
function isTokenValid(token) {
  if (!token) return false;
  const payload = decodeTokenPayload(token);
  if (!payload || !payload.exp) return false;
  return payload.exp * 1000 > Date.now();
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');

    // Validate token expiry before trusting stored session
    if (!isTokenValid(token)) {
      authAPI.logout();
      setLoading(false);
      return;
    }

    // Check for stored user or school on mount
    const storedUser = authAPI.getStoredUser();
    if (storedUser) {
      // Verify role from token payload matches stored role
      const payload = decodeTokenPayload(token);
      if (payload && payload.role && storedUser.role !== payload.role) {
        storedUser.role = payload.role; // Trust token over localStorage
      }
      setUser(storedUser);
    } else {
      // Check for school admin session
      const storedSchool = localStorage.getItem('school');
      if (storedSchool) {
        const school = JSON.parse(storedSchool);
        setUser({ id: school.id, username: school.login_username, role: 'school', school_name: school.name });
      }
    }
    setLoading(false);
  }, []);

  const login = async (credentials) => {
    const response = await authAPI.login(credentials);
    if (response._isSchool) {
      // School login — create a user-like object for auth context
      const school = response.school;
      setUser({ id: school.id, username: school.login_username, role: 'school', school_name: school.name });
    } else {
      setUser(response.user);
    }
    return response;
  };

  const signup = async (userData) => {
    const response = await authAPI.signup(userData);
    setUser(response.user);
    return response;
  };

  const logout = useCallback(() => {
    authAPI.logout();
    setUser(null);
  }, []);

  // Auto-refresh token when it's within 1 hour of expiry
  useEffect(() => {
    if (!user) return;
    const check = () => {
      const token = localStorage.getItem('token');
      if (!token) return;
      const payload = decodeTokenPayload(token);
      if (!payload || !payload.exp) return;
      const remaining = payload.exp * 1000 - Date.now();
      if (remaining < 60 * 60 * 1000 && remaining > 0) {
        // Token expires in less than 1 hour — refresh it
        fetch('/api/auth/refresh', {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        })
          .then(r => r.ok ? r.json() : null)
          .then(data => { if (data?.access_token) localStorage.setItem('token', data.access_token); })
          .catch(() => {});
      } else if (remaining <= 0) {
        logout();
      }
    };
    check();
    const interval = setInterval(check, 10 * 60 * 1000); // Check every 10 min
    return () => clearInterval(interval);
  }, [user, logout]);

  const value = {
    user,
    loading,
    login,
    signup,
    logout,
    isAuthenticated: !!user,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
